import { useForgotPassword } from '@/hooks/useAuth';
import { ForgotPasswordData } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email.invalid')
    .min(1, 'auth.validation.email.required'),
});

export const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const forgotPassword = useForgotPassword();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    try {
      await forgotPassword.mutateAsync(data);
      setIsSubmitted(true);
    } catch {
      // Error is handled by the useForgotPassword hook with toast
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center">
        <h2 className="text-lg font-medium text-neutral-900">
          {t('auth.forgotPassword.success.title')}
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          {t('auth.forgotPassword.success.message')}
        </p>
        <Link
          to="/login"
          className="mt-4 inline-flex min-h-11 items-center text-sm font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--marque-accent)' }}
        >
          {t('auth.forgotPassword.success.backToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-neutral-700"
        >
          {t('auth.forgotPassword.email')}
        </label>
        <input
          {...register('email')}
          type="email"
          id="email"
          className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-base shadow-sm placeholder:text-neutral-400/50 focus:ring-1 focus:outline-none"
          placeholder={t('auth.forgotPassword.emailPlaceholder')}
        />
        {errors.email && (
          <p className="text-error-600 mt-1 text-sm">
            {t(errors.email.message as string)}
          </p>
        )}
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="focus:ring-primary-500 flex min-h-11 w-full justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-[filter] hover:brightness-90 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
          style={{
            backgroundColor: 'var(--marque-accent)',
            color: 'var(--marque-sur-accent)',
          }}
        >
          {isSubmitting ? t('common.loading') : t('auth.forgotPassword.submit')}
        </button>
      </div>

      <div className="text-center text-sm">
        <Link
          to="/login"
          className="inline-flex min-h-11 items-center font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--marque-accent)' }}
        >
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    </form>
  );
};
